import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { updateSiteControlTarget } from '../SiteControl/siteControlApi';
import { lgPlayerId } from '../../utils/lgPlayer';
import PlayerOnlySettings from './PlayerOnlySettings';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, timeout: 20000 });
export default function LgPlayerPanel(props) {
  return <LgPlayerPanelState key={props.device.id} {...props} />;
}

function LgPlayerPanelState({ device, siteId, paired = true, onChanged, allowRename = true }) {
  const [data, setData] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [candidate, setCandidate] = useState('');
  const [name, setName] = useState(device.name || '');
  const active = useRef(true);
  const statusRequest = useRef(0);
  const refresh = useCallback(async () => {
    const request = ++statusRequest.current;
    const result = await axios.get(`/api/lg-player/${device.id}`, auth());
    if (!active.current || request !== statusRequest.current) return null;
    setData(result.data);
    return result.data;
  }, [device.id]);
  const changed = useRef(onChanged);
  changed.current = onChanged;
  const previousPlayer = useRef(lgPlayerId(device));
  const detected = useRef(false);
  const notify = () => {
    changed.current?.();
    window.dispatchEvent(new CustomEvent('lg-player-link-changed', { detail: { deviceId: device.id } }));
  };
  useEffect(() => {
    if (!data) return;
    const next = Number(data.player?.id) || null;
    if (next !== previousPlayer.current) { previousPlayer.current = next; notify(); }
  }, [data]); // Notify only when the persisted link changes, including a claim from the TV.
  useEffect(() => {
    if (!data?.installed || data.player || !paired || detected.current) return;
    detected.current = true;
    axios.post(`/api/lg-player/${device.id}/detect`, {}, auth()).catch(() => {
      if (active.current) setError('Automatic Player detection was unavailable. Use Open & connect Player to retry.');
    });
  }, [data, device.id, paired]);
  useEffect(() => {
    active.current = true;
    let timer;
    const load = async () => {
      try {
        await refresh();
      } catch (failure) { if (active.current) setError(failure.response?.data?.message || 'Could not load the Player connection.'); }
      finally { if (active.current) timer = setTimeout(load, 15000); }
    };
    load();
    return () => { active.current = false; clearTimeout(timer); };
  }, [refresh]);
  const run = async (work) => {
    if (busy) return;
    setBusy(true); setError(''); setMessage('');
    try {
      const result = await work();
      if (!active.current) return;
      setMessage(result?.message || 'Saved.');
      const refreshed = await refresh();
      if (refreshed && (Number(refreshed.player?.id) || null) === previousPlayer.current) notify();
    } catch (failure) { if (active.current) setError(failure.response?.data?.message || failure.message || 'Player setup could not be completed.'); }
    finally { if (active.current) setBusy(false); }
  };
  return <section className="dashboard-tv-remote-more-actions" aria-label="LG Player and connections">
    <h4>Player &amp; connections</h4>
    <p>LG IP control uses an Android relay. The Suitestream Player provides playback only.</p>
    <p>Player: {data?.player ? `Linked · ${data.player.name}` : data?.installed ? 'Installed — not connected' : data ? 'Not reported installed' : 'Checking…'}</p>
    <p>TV/discovery name: {data?.reportedName || 'Not reported'}</p>
    {data?.linkIssue?.message && <p role="alert">{data.linkIssue.message}</p>}
    <button type="button" disabled={busy} onClick={() => run(async () => ({ message: 'Connection refreshed.' }))}>Refresh connection</button>
    {allowRename && siteId && <label>Suitestream device name<input value={name} maxLength={255} onChange={(event) => setName(event.target.value)} disabled={busy} />
      <button type="button" disabled={busy || !name.trim() || name.trim() === data?.name} onClick={() => run(async () => {
        await updateSiteControlTarget(siteId, device.id, { name: name.trim() }); return { message: 'TV name saved.' };
      })}>Rename TV</button>
    </label>}
    <button type="button" disabled={busy || !paired || !data?.installed} onClick={() => run(async () =>
      (await axios.post(`/api/lg-player/${device.id}/open`, {}, auth())).data)}>{data?.player ? 'Launch Suitestream Player' : 'Open & connect Player'}</button>
    {!data?.player && Boolean(data?.candidates?.length) && <div>
      <label>Existing Player on this TV<select value={candidate} onChange={(event) => setCandidate(event.target.value)} disabled={busy}>
        <option value="">Choose a Player</option>{data.candidates.map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}
      </select></label>
      <p>Confirm only if this Player is running on this physical TV. Names and addresses alone do not establish that match.</p>
      <button type="button" disabled={busy || !candidate} onClick={() => run(async () =>
        (await axios.post(`/api/lg-player/${device.id}/link`, { playerDeviceId: Number(candidate) }, auth())).data)}>Link this Player to TV</button>
    </div>}
    {data?.player && <PlayerOnlySettings key={data.player.id} device={{ id: data.player.id, ...data.player.settings }} onSaved={notify} />}
    {message && <p role="status">{message}</p>}{error && <p role="alert">{error}</p>}
  </section>;
}
